import common from '../common';

/**
 * Utility class for creating nineslice buttons and reusing them if they've already been made to specification
 */
export default class ButtonFactory {
	constructor() {
		/**
		 * @type {Array<{state: ButtonStateData, texture: PIXI.Texture}>}
		 */
		this._buttonTextures = [];
	}

	/**
	 * @param {Record<string, ButtonStateData> | P3.ButtonStates} states
	 */
	createButton(states) {
		let sprites = null;

		if (states instanceof P3.ButtonStates) {
			sprites = states;
		} else {
			sprites = new P3.ButtonStates();

			const keys = Object.keys(states);

			for (let i = 0; i < keys.length; i++) {
				const state = keys[i];

				// if we're given a texture (i.e. icons), use it, otherwise, get/create a nineslice
				sprites[state] = states[state] instanceof PIXI.Texture ? states[state] : this._getTexture(states[state]);
			}
		}

		// create the button
		const button = new P3.Button(sprites);

		button.overSoundName = 'sfx_genericrollover';
		button.clickSoundName = 'sfx_genericclick';

		// accessibility
		button.accessible = true;

		return button;
	}

	/**
	 * @param {ButtonStateData} state
	 * @returns {PIXI.Texture}
	 */
	_getTexture(state) {
		if (!state) return null;

		// check each state and see if we already have a texture for it
		let texture = this._buttonTextures.find((t) => t.state.textureName === state.textureName && t.state.width === state.width && t.state.height === state.height && t.state.left === state.left && t.state.top === state.top && t.state.right === state.right && t.state.bottom === state.bottom);

		if (!texture) {
			texture = this._createTexture(state);
			this._buttonTextures.push(texture);
		}

		return texture.texture;
	}

	/**
	 * @param {ButtonStateData} state
	 * @returns {{state: ButtonStateData, texture: PIXI.Texture}}
	 */
	_createTexture(state) {
		const nineslice = new PIXI.NineSlicePlane(P3.assets.texture(state.textureName), state.left, state.top, state.right, state.bottom);

		nineslice.width = state.width;
		nineslice.height = state.height;

		const texture = common.renderer.generateTexture(nineslice);

		return { state, texture };
	}
}

/**
 * @typedef {Object} ButtonStateData
 */

class ButtonStateData {
	/**
	 * @param {String} textureName
	 * @param {Number} width
	 * @param {Number} height
	 * @param {Number} left
	 * @param {Number} top
	 * @param {Number} right
	 * @param {Number} bottom
	 */
	constructor(textureName, width, height, left, top, right, bottom) {
		/**
		 * @type {String}
		 */
		this.textureName = textureName;

		/**
		 * @type {Number}
		 */
		this.width = width;

		/**
		 * @type {Number}
		 */
		this.height = height;

		/**
		 * @type {Number}
		 */
		this.left = left;

		/**
		 * @type {Number}
		 */
		this.top = top;

		/**
		 * @type {Number}
		 */
		this.right = right;

		/**
		 * @type {Number}
		 */
		this.bottom = bottom;
	}
}
